import React, { useState, useEffect, useRef } from "react";
import { ChevronUp, ArrowLeft, Plus, Eye } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Import your icon assets here
import Icon1 from "@/assets/home/card/Icon1.svg";
import Icon2 from "@/assets/home/card/Icon2.svg";
import Icon3 from "@/assets/home/card/Icon3.svg";
import Icon4 from "@/assets/home/card/Icon4.svg";
import Icon5 from "@/assets/home/card/Icon5.svg";
import Icon6 from "@/assets/home/card/Icon6.svg";
import Dropdown, { Options } from "@/components/ui/DropDown";

import CompanyLogo from "@/assets/company-logo.png";
import useCampaignDetailStore from "../store/useCampaignDetailStore";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";
import "../css/index.css";
import Button from "@/components/ui/Button";

// Type definitions
interface AssetsCardProps {
  imgUrl: string;
  progress1: number;
  progress2: number;
  progress3: number;
  progress4: number;
  progress5: number;
  progress6: number;
  campaignId: string;
  platformName?: string;
  count?: number;
  aspectRatio?: string; // New prop for dynamic aspect ratio (e.g., "16/9", "4/3", "1/1")
}

interface PlatformCard {
  id: string;
  name: string;
  count: number;
  imgUrl: string;
  progress: {
    progress1: number;
    progress2: number;
    progress3: number;
    progress4: number;
    progress5: number;
    progress6: number;
  };
  campaignId: string;
  isPlaceholder?: boolean;
  aspectRatio?: string; // New field for aspect ratio
}

interface Section {
  title: string;
  count: number;
  colorBar: string[];
  platforms: PlatformCard[];
}

// AssetsCard Component with Dynamic Aspect Ratio
const AssetsCard: React.FC<AssetsCardProps> = ({
  imgUrl,
  progress1,
  progress2,
  progress3,
  progress4,
  progress5,
  progress6,
  campaignId,
  platformName,
  count,
  aspectRatio = "auto", // Default to "auto" if not specified
}) => {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [calculatedRatio, setCalculatedRatio] = useState<string>("auto");
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Calculate aspect ratio from image if aspectRatio is "auto"
  useEffect(() => {
    if (aspectRatio === "auto" && imgUrl) {
      const img = new Image();
      img.src = imgUrl;
      img.onload = () => {
        const ratio = img.width / img.height;
        // Convert to common ratio format
        let ratioString = "1 / 1"; // Default square

        if (ratio > 1.7) {
          ratioString = "16 / 9"; // Wide
        } else if (ratio > 1.4) {
          ratioString = "3 / 2"; // Medium wide
        } else if (ratio > 1.2) {
          ratioString = "4 / 3"; // Slightly wide
        } else if (ratio > 0.9 && ratio < 1.1) {
          ratioString = "1 / 1"; // Square
        } else if (ratio < 0.8) {
          ratioString = "9 / 16"; // Portrait
        }

        setCalculatedRatio(ratioString);
      };
    } else if (aspectRatio !== "auto") {
      setCalculatedRatio(aspectRatio);
    }
  }, [imgUrl, aspectRatio]);

  const icons = [
    [
      { src: Icon2, alt: "icon 2", number: progress1 },
      { src: Icon3, alt: "icon 3", number: progress2 },
      { src: Icon4, alt: "icon 4", number: progress3 },
    ],
    [
      { src: Icon1, alt: "icon 1", number: progress4 },
      { src: Icon6, alt: "icon 6", number: progress5 },
      { src: Icon5, alt: "icon 5", number: progress6 },
    ],
  ];

  return (
    <div
      className="w-full rounded-2xl relative hover-card overflow-hidden"
      style={{
        aspectRatio: calculatedRatio,
        minHeight: "330px",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Background Image with Zoom Effect */}
      <div
        className="bg-cover bg-no-repeat bg-center w-full h-full absolute inset-0 transition-transform duration-500 ease-out"
        style={{
          backgroundImage: `url(${imgUrl})`,
          transform: hover ? "scale(1.1)" : "scale(1)",
        }}
      />

      {/* Overlay */}
      <div
        className={`w-full h-full p-0 transition-opacity duration-300 absolute inset-0 rounded-2xl hover-overlay ${
          hover ? "opacity-100 bg-black bg-opacity-70" : "opacity-0"
        }`}
      >
        <div className="p-0 flex w-full h-full flex-col gap-7 justify-center items-center card-drop rounded-2xl">
          <div className="flex w-full justify-center z-10 relative">
            {icons.map((iconGroup, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-3.5 items-center justify-center"
              >
                {iconGroup.map(({ src, alt, number }, i) => (
                  <div key={i} className="flex mr-6 ml-4 my-1">
                    <img
                      src={src}
                      alt={alt}
                      className="w-8 h-8 filter drop-shadow-lg"
                    />
                    <p className="w-8 h-8 flex justify-center items-center text-white">
                      {number}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <Button
            className="z-10 relative"
            type="outline"
            trailingIcon={<Eye />}
            onClick={() => {
              navigate(`/campaign/${campaignId}/ad-builder`);
            }}
          >
            View Assets
          </Button>
        </div>
      </div>
    </div>
  );
};

const CampaignDetailAdCategoryDetails: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams] = useSearchParams();

  const languageId = searchParams.get("languageId");
  const objectiveId = searchParams.get("objectiveId");

  const { showSuccess, showError } = useGlobalStore();

  const {
    languageObjectiveDashboard,
    getLanguageObjectiveDashboard,
    putS3Image,
    uploadAssetsDashboardImage,
  } = useCampaignDetailStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Updated to use string values for the custom Dropdown component
  const [filters, setFilters] = useState({
    status: "",
    region: "",
    publication: "",
  });

  const fetchData = async () => {
    if (!campaignId || !languageId || !objectiveId) {
      showError(
        "Missing required parameters: campaignId, languageId, or objectiveId",
      );
      return;
    }

    try {
      setIsLoading(true);
      await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);
    } catch (error) {
      showError(error.message ?? "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle thumbnail upload
  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  // Handle thumbnail upload (updated to support initiate → PUT → confirm flow)
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !campaignId || !languageId || !objectiveId) return;

    try {
      setIsUploading(true);

      // 🧩 Step 1 — Initiate upload (ask backend for presigned URL)
      const initiateBody = {
        campaign_id: campaignId,
        type: "assets_dashboard",
        objective_id: objectiveId,
        language_id: languageId,
        filename: file.name, // ✅ send filename instead of key
        mime: file.type,
      };

      const initiateResponse = await uploadAssetsDashboardImage(
        campaignId,
        initiateBody,
      );

      // 🧩 Step 2 — Upload to S3 using presigned URL
      const uploadUrl =
        "upload_url" in initiateResponse.data
          ? initiateResponse.data.upload_url
          : null;

      if (!uploadUrl) {
        throw new Error("Upload URL missing in initiation response");
      }

      await putS3Image(uploadUrl, file);

      // 🧩 Step 3 — Confirm upload to backend
      const confirmBody = {
        campaign_id: campaignId,
        type: "assets_dashboard",
        objective_id: objectiveId,
        language_id: languageId,
        key: initiateResponse.data.key, // ✅ backend expects the key after upload
        mime: file.type,
      };

      await uploadAssetsDashboardImage(campaignId, confirmBody);

      // Optionally refresh the dashboard data to show new thumbnail
      // await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);

      showSuccess("Image uploaded and confirmed successfully!");
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to upload thumbnail. Please try again.";
      showError(errorMessage);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // const handleFileChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];
  //   if (!file || !campaignId || !languageId || !objectiveId) return;

  //   try {
  //     setIsUploading(true);

  //     // Generate the key for the image
  //     const key = `${campaignId}/${file.name}`;

  //     // Prepare the request body
  //     const requestBody = {
  //       campaign_id: campaignId,
  //       type: "assets_dashboard",
  //       objective_id: objectiveId,
  //       language_id: languageId,
  //       key: key,
  //       mime: file.type,
  //     };

  //     // First, call the backend to confirm the header image
  //     const response = await uploadAssetsDashboardImage(
  //       campaignId,
  //       requestBody
  //     );

  //     // If the response contains a presigned URL, upload the file to S3
  //     if (response.data.url) {
  //       await putS3Image(response.data.url, file);
  //     }

  //     // Optionally refresh the dashboard data to show the new image
  //     // await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);

  //     showSuccess("Image uploaded successfully!");
  //   } catch (error) {
  //     console.error("Error uploading thumbnail:", error);
  //     const errorMessage =
  //       error instanceof Error
  //         ? error.message
  //         : "Failed to upload thumbnail. Please try again.";
  //     showError(errorMessage);
  //   } finally {
  //     setIsUploading(false);
  //     // Reset file input
  //     if (fileInputRef.current) {
  //       fileInputRef.current.value = "";
  //     }
  //   }
  // };

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchData();
  }, [campaignId, languageId, objectiveId]);

  // Auto-expand all sections on load
  useEffect(() => {
    if (languageObjectiveDashboard?.nested_by_ad_category) {
      const expanded: Record<string, boolean> = {};
      languageObjectiveDashboard.nested_by_ad_category.forEach((_, index) => {
        expanded[`section-${index}`] = true;
      });
      setExpandedSections(expanded);
    }
  }, [languageObjectiveDashboard]);

  // Get unique regions from nested_by_flight data
  const availableRegions = Array.from(
    new Set(
      languageObjectiveDashboard?.nested_by_flight?.flatMap((flight) =>
        flight.flight_rows?.map((row) => row?.region),
      ) || [],
    ),
  );

  // Get unique ad category names (publications) from nested_by_ad_category
  const availablePublications = Array.from(
    new Set(
      languageObjectiveDashboard?.nested_by_ad_category?.map(
        (category) => category?.ad_category_name,
      ) || [],
    ),
  );

  // Filter options for the dropdowns
  const filterOptions: Record<string, Options[]> = {
    status: [
      { title: "Active", value: "active" },
      { title: "Draft", value: "draft" },
      { title: "Completed", value: "completed" },
    ],
    region: availableRegions.map((region) => ({
      title: region,
      value: region?.toLowerCase().replace(/\s+/g, "-"),
    })),
    publication: availablePublications.map((publication) => ({
      title: publication,
      value: publication.toLowerCase().replace(/\s+/g, "-"),
    })),
  };

  // Generate color bar from ad types
  const generateColorBar = (adTypes: any[]) => {
    const colors = [
      "#fde047",
      "#f97316",
      "#ef4444",
      "#a855f7",
      "#3b82f6",
      "#06b6d4",
      "#fb923c",
      "#10b981",
    ];
    return adTypes.map((_, index) => colors[index % colors.length]);
  };

  // Convert API data to sections
  const allSections: Section[] =
    languageObjectiveDashboard?.nested_by_ad_category.map((category) => ({
      title: category.ad_category_name,
      count: category.total_ads,
      colorBar: generateColorBar(category.ad_types),
      platforms: category.ad_types.map((adType) => ({
        id: adType.ad_type_id,
        name: adType.ad_type_name,
        count: adType.fake_counts.impressions,
        imgUrl: CompanyLogo, // Use default image for now
        progress: {
          progress1: adType.fake_counts.impressions,
          progress2: adType.fake_counts.clicks,
          progress3: Math.floor(Math.random() * 10), // Placeholder
          progress4: Math.floor(Math.random() * 10), // Placeholder
          progress5: Math.floor(Math.random() * 10), // Placeholder
          progress6: Math.floor(Math.random() * 10), // Placeholder
        },
        campaignId: campaignId || "",
        aspectRatio: adType.ratio || "1 / 1",
      })),
    })) || [];

  // Filter sections based on selected filters
  const sections = allSections.filter((section) => {
    // Filter by publication (ad category name pattern matching)
    if (filters.publication) {
      const publicationMatch = section.title
        .toLowerCase()
        .includes(filters.publication.toLowerCase());
      if (!publicationMatch) return false;
    }

    // Filter by status - using campaign status from metadata
    if (filters.status) {
      const campaignStatus =
        languageObjectiveDashboard?.metadata.campaign.status;
      if (campaignStatus !== filters.status) return false;
    }

    // Filter by region - check if any platform has assets in the selected region
    if (filters.region) {
      const hasRegionMatch = languageObjectiveDashboard?.nested_by_flight.some(
        (flight) =>
          flight.flight_rows.some(
            (row) =>
              row.region.toLowerCase().replace(/\s+/g, "-") ===
                filters.region &&
              row.flight_ad_categories.some((cat) =>
                section.platforms.some((platform) =>
                  cat.ad_rows.some((ad) => ad.ad_type === platform.name),
                ),
              ),
          ),
      );
      if (!hasRegionMatch) return false;
    }

    return true;
  });

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const PlaceholderCard: React.FC = () => (
    <div className="bg-white rounded-2xl p-8 flex items-center justify-center h-[330px] border-2 border-gray-100">
      <button className="flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 transition-colors">
        <Plus size={48} strokeWidth={1} />
        <span className="mt-2 text-sm">Add content</span>
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!languageObjectiveDashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">No dashboard data found</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-semibold">
            {languageObjectiveDashboard?.metadata.objective.name || "Objective"}
          </h1>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleThumbnailClick}
            disabled={isUploading}
            className="text-gray-500 hover:text-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-2 text-sm underline">
              <span className="mr-3">
                {isUploading ? "Uploading..." : "Edit Thumbnail"}
              </span>
              <img
                src={
                  languageObjectiveDashboard?.metadata.campaign
                    .campaign_poster || CompanyLogo
                }
                className="w-10 h-10 rounded"
              />
            </span>
          </button>
        </div>
      </div>

      {/* Filters - Updated to use custom Dropdown component */}
      <div className="flex items-center gap-4 mb-8 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Filter by:</span>

        {/* Status Filter */}
        <Dropdown
          selected={filters.status}
          setSelected={(value) =>
            setFilters((prev) => ({ ...prev, status: value.toString() }))
          }
          options={filterOptions.status}
          placeholder="Status"
          className="w-48"
        />

        {/* Region Filter */}
        <Dropdown
          selected={filters.region}
          setSelected={(value) =>
            setFilters((prev) => ({ ...prev, region: value.toString() }))
          }
          options={filterOptions.region}
          placeholder="Region"
          className="w-48"
        />

        {/* Publication Filter */}
        <Dropdown
          selected={filters.publication}
          setSelected={(value) =>
            setFilters((prev) => ({ ...prev, publication: value.toString() }))
          }
          options={filterOptions.publication}
          placeholder="Publication"
          className="w-48"
        />

        {/* Clear Filters Button */}
        {(filters.status || filters.region || filters.publication) && (
          <button
            onClick={() =>
              setFilters({ status: "", region: "", publication: "" })
            }
            className="text-sm text-pink-600 hover:text-pink-700 underline font-medium"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-2">No categories found</p>
            <p className="text-sm">
              Try adjusting your filters to see more results
            </p>
            {(filters.status || filters.region || filters.publication) && (
              <button
                onClick={() =>
                  setFilters({ status: "", region: "", publication: "" })
                }
                className="mt-4 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          sections.map((section, index) => {
            const sectionKey = `section-${index}`;
            const isExpanded = expandedSections[sectionKey];

            return (
              <div key={sectionKey} className="rounded-xl">
                {/* Section Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {section.title}
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-2 rounded-full overflow-hidden"
                        style={{ width: "120px" }}
                      >
                        {section.colorBar.map((color, index) => (
                          <div
                            key={index}
                            className="h-full"
                            style={{
                              backgroundColor: color,
                              width: `${100 / section.colorBar.length}%`,
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-lg font-semibold text-gray-700">
                        {section.count}
                      </span>
                    </div>
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <ChevronUp
                        size={20}
                        className={`transition-transform duration-200 ${
                          isExpanded ? "rotate-0" : "rotate-180"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <hr className="mt-2 border-1 border-gray-300 mb-2" />

                {/* Platform Cards Grid - Masonry Layout */}
                {isExpanded && section.platforms.length > 0 && (
                  <div
                    className="masonry-grid"
                    style={{
                      columnCount: 4,
                      columnGap: "1rem",
                    }}
                  >
                    {section.platforms.map((platform) =>
                      platform.isPlaceholder ? (
                        <div key={platform.id} className="masonry-item">
                          <PlaceholderCard />
                        </div>
                      ) : (
                        <div key={platform.id} className="masonry-item">
                          <AssetsCard
                            imgUrl={platform.imgUrl}
                            progress1={platform.progress.progress1}
                            progress2={platform.progress.progress2}
                            progress3={platform.progress.progress3}
                            progress4={platform.progress.progress4}
                            progress5={platform.progress.progress5}
                            progress6={platform.progress.progress6}
                            campaignId={platform.campaignId}
                            platformName={platform.name}
                            count={platform.count}
                            aspectRatio={platform.aspectRatio}
                          />
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CampaignDetailAdCategoryDetails;
